import React, { useState } from "react";
import toast from "react-hot-toast";
import { extractCompanyJd, structureJdCriteria } from "../services/jobService";
import PipelineBuilder, { defaultPipelineStages } from "./PipelineBuilder";
import StageThresholdsEditor, { defaultThresholds } from "./StageThresholdsEditor";
import "./RecruiterJobs.css";

// Mirrors resume_scoring.py's JD_EXTRACTION_PROMPT_TEMPLATE schema exactly —
// keep both in sync if that schema changes.
const EMPTY_CRITERIA = {
  job_title: "", company_name: "", department: "", location: "", employment_type: "",
  min_experience_years: null, max_experience_years: null,
  minimum_education: "", preferred_education: "", role_summary: "",
  key_responsibilities: [], mandatory_requirements: [],
  required_skills: [], preferred_skills: [],
  certifications: [], relevant_domains: [], technologies_tools: []
};

// Plain-string-array fields editable as an add/remove chip list.
const LIST_FIELDS = [
  { key: "required_skills", label: "Required skills", prompt: "Add a required skill" },
  { key: "preferred_skills", label: "Preferred skills", prompt: "Add a preferred skill" },
  { key: "key_responsibilities", label: "Key responsibilities", prompt: "Add a responsibility" },
  { key: "mandatory_requirements", label: "Mandatory requirements", prompt: "Add a mandatory requirement" },
  { key: "certifications", label: "Certifications", prompt: "Add a certification" },
  { key: "relevant_domains", label: "Relevant domains", prompt: "Add a domain" },
  { key: "technologies_tools", label: "Technologies & tools", prompt: "Add a technology or tool" }
];

const DEFAULT_STAGE_CONFIG = {
  minScore: 60, autoShortlistThreshold: 75, mcqMinutes: 25, codingMinutes: 60, aiMinutes: 25,
  rankingWeights: { ai: 40, coding: 25, mcq: 15, resume: 10, integrity: 10 }
};

function ListChipsEditor({ label, prompt, values, onChange }) {
  const add = () => {
    const value = window.prompt(prompt);
    if (!value || !value.trim()) return;
    onChange([...(values || []), value.trim()]);
  };
  const remove = (index) => onChange(values.filter((_, i) => i !== index));

  return (
    <div className="pipeline-skill-row">
      <span className="job-form-block-label">{label}</span>
      <div className="pipeline-skill-chips">
        {(values || []).map((value, i) => (
          <span key={`${value}-${i}`} className="rd-tag pipeline-skill-chip">
            {value}
            <button type="button" onClick={() => remove(i)} aria-label={`Remove ${value}`}>×</button>
          </span>
        ))}
        <button type="button" className="pipeline-skill-add" onClick={add}>+ Add</button>
      </div>
    </div>
  );
}

// New multi-step hiring-pipeline job creation flow. This is deliberately a
// separate component from CreateJobModal.jsx (which stays untouched for
// simple single-assessment jobs) — a "Full hiring pipeline" job additionally
// carries pipelineStages/stageConfig/resumeCriteria, consumed by the new
// PipelineCandidateTable instead of the legacy MCQ/Interview results views.
export default function CreateJobWizard({ open, saving, onClose, onCreate }) {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [mode, setMode] = useState("upload"); // upload | manual
  const [jdExtracting, setJdExtracting] = useState(false);
  const [jdText, setJdText] = useState("");
  // Tracks whether structureJdCriteria actually succeeded — jdText alone
  // isn't enough, since JD text extraction and criteria structuring are two
  // separate calls that can fail independently (e.g. the JD text loads fine
  // but the AI-extraction call fails because no LLM key is configured).
  const [criteriaReady, setCriteriaReady] = useState(false);
  const [criteria, setCriteria] = useState(EMPTY_CRITERIA);
  const [stages, setStages] = useState(defaultPipelineStages());
  const [stageConfig, setStageConfig] = useState(DEFAULT_STAGE_CONFIG);
  // Only the recruiter's edits; merged over per-stage defaults at render/submit
  // so adding/removing stages in step 2 never leaves stale entries behind.
  const [thresholdEdits, setThresholdEdits] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const reset = () => {
    setStep(1); setTitle(""); setCompanyName(""); setMode("upload");
    setJdText(""); setCriteria(EMPTY_CRITERIA); setCriteriaReady(false);
    setStages(defaultPipelineStages());
    setStageConfig(DEFAULT_STAGE_CONFIG); setThresholdEdits({}); setError("");
  };
  const handleClose = () => { reset(); onClose(); };

  const updateCriteria = (field, value) => setCriteria((c) => ({ ...c, [field]: value }));

  const handleJdFile = async (file) => {
    if (!file) return;
    setJdExtracting(true); setError(""); setCriteriaReady(false);
    let extractedText = "";
    try {
      const extracted = await extractCompanyJd(file);
      extractedText = extracted.text || "";
      setJdText(extractedText);
    } catch (err) {
      setJdExtracting(false);
      toast.error(err.message || "Unable to read that file");
      return;
    }
    try {
      const structured = await structureJdCriteria(extractedText);
      setCriteria({ ...EMPTY_CRITERIA, ...structured });
      setCriteriaReady(true);
      // Prefill the job's own title/company only if the recruiter hasn't typed one yet.
      setTitle((t) => t || structured.job_title || "");
      setCompanyName((c) => c || structured.company_name || "");
    } catch (err) {
      // The JD text itself did load (jdText is set) — only the AI field
      // extraction failed. Tell the recruiter exactly why (e.g. "AI
      // extraction is not configured on the server") instead of leaving them
      // staring at blank fields with no explanation.
      toast.error(err.message || "Unable to extract fields from this JD. You can still fill them in manually below.");
    } finally {
      setJdExtracting(false);
    }
  };

  const goNext = () => {
    setError("");
    if (step === 1 && !title.trim()) { setError("Job title is required."); return; }
    if (step === 1 && !companyName.trim()) { setError("Company name is required."); return; }
    setStep((s) => Math.min(3, s + 1));
  };

  const thresholds = Object.fromEntries(
    Object.entries({ ...defaultThresholds(stages, stageConfig), ...thresholdEdits })
      .filter(([stage]) => stages.includes(stage))
  );
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleCreate = async () => {
    setError(""); setSubmitting(true);
    try {
      await onCreate({
        title, companyName,
        mode: "random_ai",
        eventType: "INTERVIEW",
        uploadedQuestions: [],
        config: {},
        pipelineStages: stages,
        stageConfig: { ...stageConfig, thresholds, publicBaseUrl: window.location.origin },
        resumeCriteria: { ...criteria, job_title: criteria.job_title || title, company_name: criteria.company_name || companyName }
      });
      toast.success("Pipeline job created");
      handleClose();
    } catch (err) {
      setError(err.message || "Unable to create job.");
    } finally {
      setSubmitting(false);
    }
  };

  const busy = saving || submitting;
  const steps = [
    { n: 1, label: "Job information", sub: "JD or manual" },
    { n: 2, label: "Pipeline", sub: "Drag features" },
    { n: 3, label: "Review", sub: "Create & launch" }
  ];

  return (
    <div className="recruiter-modal-backdrop" role="presentation">
      <div className="recruiter-modal recruiter-modal--wide pipeline-wizard" role="dialog" aria-modal="true" aria-labelledby="create-pipeline-title">
        <div className="recruiter-modal-header">
          <div>
            <h2 id="create-pipeline-title">Create Hiring Pipeline</h2>
            <p>Step {step} of 3 — {steps[step - 1].label}</p>
          </div>
          <button className="icon-button" type="button" onClick={handleClose} aria-label="Close">x</button>
        </div>

        <div className="pipeline-wizard-steps">
          {steps.map((s) => (
            <button
              key={s.n}
              type="button"
              className={`pipeline-wizard-step${step === s.n ? " active" : step > s.n ? " done" : ""}`}
              onClick={() => setStep(s.n)}
            >
              <span className="pipeline-wizard-step-num">{step > s.n ? "✓" : s.n}</span>
              <span className="pipeline-wizard-step-copy"><strong>{s.label}</strong><small>{s.sub}</small></span>
            </button>
          ))}
        </div>

        <div className="job-form pipeline-wizard-body">
          {error && <div className="job-form-error">{error}</div>}

          {step === 1 && (
            <>
              <label>
                <span>Job Title</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="React Native Developer" />
              </label>
              <label>
                <span>Company Name</span>
                <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Vagmee" />
              </label>

              <div className="inst-type-toggle">
                <button type="button" className={`inst-type-btn${mode === "upload" ? " active" : ""}`} onClick={() => setMode("upload")}>Upload JD</button>
                <button type="button" className={`inst-type-btn${mode === "manual" ? " active" : ""}`} onClick={() => setMode("manual")}>Create Manually</button>
              </div>

              {mode === "upload" && (
                <div className="job-form-block">
                  <label className="pipeline-jd-dropzone">
                    <input
                      type="file" accept=".pdf,.docx" style={{ display: "none" }}
                      onChange={(e) => handleJdFile(e.target.files?.[0])}
                    />
                    {jdExtracting
                      ? "Extracting fields…"
                      : criteriaReady
                        ? "JD loaded — fields extracted below. Click to replace."
                        : jdText
                          ? "JD text loaded, but AI field extraction failed — see error above. Click to retry, or fill fields in manually."
                          : "Click to upload a PDF or DOCX job description"}
                  </label>
                </div>
              )}

              <div className="job-form-block">
                <span className="job-form-block-label">Scoring criteria {mode === "manual" && "(edit directly)"}</span>
                <div className="pipeline-criteria-grid">
                  <label><span>Department</span><input value={criteria.department} onChange={(e) => updateCriteria("department", e.target.value)} /></label>
                  <label><span>Location</span><input value={criteria.location} onChange={(e) => updateCriteria("location", e.target.value)} /></label>
                  <label><span>Employment Type</span><input value={criteria.employment_type} onChange={(e) => updateCriteria("employment_type", e.target.value)} /></label>
                  <label>
                    <span>Min Experience (yrs)</span>
                    <input type="number" value={criteria.min_experience_years ?? ""} onChange={(e) => updateCriteria("min_experience_years", e.target.value === "" ? null : Number(e.target.value))} />
                  </label>
                  <label>
                    <span>Max Experience (yrs)</span>
                    <input type="number" value={criteria.max_experience_years ?? ""} onChange={(e) => updateCriteria("max_experience_years", e.target.value === "" ? null : Number(e.target.value))} />
                  </label>
                  <label><span>Minimum Education</span><input value={criteria.minimum_education} onChange={(e) => updateCriteria("minimum_education", e.target.value)} /></label>
                  <label><span>Preferred Education</span><input value={criteria.preferred_education} onChange={(e) => updateCriteria("preferred_education", e.target.value)} /></label>
                  <label className="pipeline-criteria-wide"><span>Role Summary</span><textarea rows={2} value={criteria.role_summary} onChange={(e) => updateCriteria("role_summary", e.target.value)} /></label>
                </div>

                {LIST_FIELDS.map(({ key, label, prompt }) => (
                  <ListChipsEditor
                    key={key}
                    label={label}
                    prompt={prompt}
                    values={criteria[key]}
                    onChange={(next) => updateCriteria(key, next)}
                  />
                ))}
              </div>
            </>
          )}

          {step === 2 && (
            <PipelineBuilder stages={stages} onChange={setStages} />
          )}

          {step === 3 && (
            <div className="pipeline-review">
              <div className="pipeline-review-summary">
                <strong>{title || "Untitled role"}</strong>
                <span className="rd-muted">
                  {companyName} · {criteria.location || "Location TBD"} · {criteria.min_experience_years ?? "0"}–{criteria.max_experience_years ?? "?"} yrs
                </span>
              </div>
              <div className="pipeline-review-stages">
                {stages.map((key, i) => (
                  <span key={`${key}-${i}`} className="rd-tag">{i + 1}. {key.toUpperCase()}</span>
                ))}
              </div>
              <div className="job-form-block">
                <span className="job-form-block-label">Ranking weight — AI Interview</span>
                <input
                  type="range" min={0} max={100} value={stageConfig.rankingWeights.ai}
                  onChange={(e) => setStageConfig((c) => ({ ...c, rankingWeights: { ...c.rankingWeights, ai: Number(e.target.value) } }))}
                />
                <span className="rd-muted">{stageConfig.rankingWeights.ai}% — remaining weight auto-normalizes across the stages this pipeline actually contains.</span>
              </div>
              <div className="job-form-block">
                <span className="job-form-block-label">Automation — thresholds per stage</span>
                <p className="rd-muted" style={{ margin: "2px 0 10px" }}>
                  Turn on auto-advance for a stage and candidates who meet every minimum move to the next stage and get its invite by email automatically. You can change these later from the pipeline.
                </p>
                <StageThresholdsEditor inline stages={stages} thresholds={thresholds} onChange={setThresholdEdits} />
              </div>
            </div>
          )}
        </div>

        <div className="recruiter-modal-actions">
          <button className="secondary-button" type="button" onClick={handleClose}>Cancel</button>
          {step > 1 && <button className="secondary-button" type="button" onClick={goBack}>Back</button>}
          {step < 3 ? (
            <button className="primary-button" type="button" onClick={goNext}>Continue</button>
          ) : (
            <button className="primary-button" type="button" disabled={busy} onClick={handleCreate}>
              {busy ? "Creating…" : "Create Job & Start Hiring"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
